import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { generateTrackingLink, applyTrackingTemplate } from "@/lib/tracking";
import { useStatuses } from "@/context/StatusContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ExternalLink, Loader2, AtSign } from "lucide-react";

const EMPTY = {
  order_id: "", phone: "", tracking_number: "", courier_partner: "", city: "", state: "", payment_method: "",
  country: "India", zip_code: "", status: "", stage: "open", pickup_date: "", initial_edd: "", notes: "", assigned_to: "",
};

function toDateInput(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" }); } catch { return ""; }
}

export default function ShipmentForm({ open, onOpenChange, editing, agents, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [couriers, setCouriers] = useState([]);
  const [countries, setCountries] = useState([]);
  const { statuses, stages } = useStatuses();

  const notesRef = useRef(null);
  const [tagged, setTagged] = useState([]);
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionQuery, setMentionQuery] = useState("");
  const [mentionStart, setMentionStart] = useState(0);
  const [mentionIndex, setMentionIndex] = useState(0);
  const team = agents || [];

  useEffect(() => {
    api.get("/couriers").then((r) => setCouriers(r.data)).catch(() => {});
    api.get("/countries").then((r) => setCountries(r.data)).catch(() => {});
  }, []);

  // Auto-fill state (locked) + city from the PIN code
  useEffect(() => {
    const pin = (form.zip_code || "").replace(/\D/g, "");
    if (pin.length < 5) return;
    let active = true;
    const t = setTimeout(() => {
      api.get(`/pincode/${pin}`, { params: { country: form.country || "India" } }).then((r) => {
        if (!active) return;
        setForm((f) => ({ ...f, state: r.data.state || "", city: r.data.city || "" }));
      }).catch(() => {});
    }, 400);
    return () => { active = false; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.zip_code, form.country]);

  useEffect(() => {
    setTagged([]);
    setMentionOpen(false);
    if (editing) {
      setForm({
        ...EMPTY,
        ...editing,
        pickup_date: toDateInput(editing.pickup_date),
        initial_edd: toDateInput(editing.initial_edd),
        assigned_to: editing.assigned_to || "",
      });
    } else {
      setForm(EMPTY);
    }
  }, [editing, open]);

  const mentionMatches = mentionOpen
    ? team.filter((m) => m.name.toLowerCase().includes(mentionQuery.toLowerCase())).slice(0, 6)
    : [];

  const handleNotesChange = (e) => {
    const val = e.target.value;
    setForm((f) => ({ ...f, notes: val }));
    const pos = e.target.selectionStart;
    const before = val.slice(0, pos);
    const m = before.match(/(?:^|\s)@([^\s@]*)$/);
    if (m) {
      setMentionQuery(m[1]);
      setMentionStart(pos - m[1].length - 1);
      setMentionIndex(0);
      setMentionOpen(true);
    } else {
      setMentionOpen(false);
    }
  };

  const insertMention = (member) => {
    const notes = form.notes || "";
    const after = notes.slice(mentionStart + 1 + mentionQuery.length);
    const before = notes.slice(0, mentionStart);
    setForm((f) => ({ ...f, notes: `${before}@${member.name} ${after}` }));
    setTagged((t) => (t.includes(member.id) ? t : [...t, member.id]));
    setMentionOpen(false);
    setTimeout(() => notesRef.current?.focus(), 0);
  };

  const handleNotesKeyDown = (e) => {
    if (!mentionOpen || mentionMatches.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setMentionIndex((i) => (i + 1) % mentionMatches.length); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setMentionIndex((i) => (i - 1 + mentionMatches.length) % mentionMatches.length); }
    else if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); insertMention(mentionMatches[mentionIndex]); }
    else if (e.key === "Escape") { setMentionOpen(false); }
  };

  const trackingLink = useMemo(() => {
    const c = couriers.find((x) => x.name === form.courier_partner);
    if (c?.tracking_url_template) return applyTrackingTemplate(c.tracking_url_template, form.tracking_number, form.order_id);
    return generateTrackingLink(form.courier_partner, form.tracking_number);
  }, [couriers, form.courier_partner, form.tracking_number, form.order_id]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    if (!form.order_id || !form.tracking_number || !form.courier_partner) {
      toast.error("Order ID, tracking number and courier partner are required");
      return;
    }
    if (!form.country) {
      toast.error("Please select a country");
      return;
    }
    if (!form.payment_method) {
      toast.error("Please select a payment method (Prepaid or COD)");
      return;
    }
    if (!form.status) {
      toast.error("Please select the final action required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        pickup_date: form.pickup_date ? new Date(form.pickup_date).toISOString() : null,
        initial_edd: form.initial_edd ? new Date(form.initial_edd).toISOString() : null,
        assigned_to: form.assigned_to || null,
        tagged_user_ids: editing ? undefined : tagged,
      };
      if (editing) {
        await api.put(`/shipments/${editing.id}`, payload);
        toast.success("Shipment updated");
      } else {
        await api.post("/shipments", payload);
        toast.success("Shipment added");
      }
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="shipment-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{editing ? "Edit shipment" : "Add shipment"}</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
          <div>
            <Label>Order ID *</Label>
            <Input data-testid="form-order-id" value={form.order_id} onChange={set("order_id")} className="mt-1.5" />
          </div>
          <div>
            <Label>Customer Phone</Label>
            <Input data-testid="form-phone" value={form.phone} onChange={set("phone")} className="mt-1.5" placeholder="e.g. 9876543210" />
          </div>
          <div>
            <Label>Tracking Number *</Label>
            <Input data-testid="form-tracking-number" value={form.tracking_number} onChange={set("tracking_number")} className="mt-1.5" />
          </div>
          <div>
            <Label>Courier Partner *</Label>
            <Select value={form.courier_partner || ""}
                    onValueChange={(v) => setForm((f) => ({ ...f, courier_partner: v }))}>
              <SelectTrigger data-testid="form-courier" className="mt-1.5"><SelectValue placeholder="Select courier" /></SelectTrigger>
              <SelectContent>
                {couriers.map((c) => (
                  <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Payment Method *</Label>
            <Select value={form.payment_method || ""} onValueChange={(v) => setForm((f) => ({ ...f, payment_method: v }))}>
              <SelectTrigger data-testid="form-payment" className="mt-1.5"><SelectValue placeholder="Select payment" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="prepaid">Prepaid</SelectItem>
                <SelectItem value="cod">COD</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Status *</Label>
            <Select value={form.stage || "open"} onValueChange={(v) => setForm((f) => ({ ...f, stage: v }))}>
              <SelectTrigger data-testid="form-stage" className="mt-1.5"><SelectValue placeholder="Select status" /></SelectTrigger>
              <SelectContent>
                {stages.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Final action required *</Label>
            <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
              <SelectTrigger data-testid="form-status" className="mt-1.5"><SelectValue placeholder="Select the final action required" /></SelectTrigger>
              <SelectContent>
                {statuses.map((s) => (
                  <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Country *</Label>
            <Select value={form.country || ""} onValueChange={(v) => setForm((f) => ({ ...f, country: v }))}>
              <SelectTrigger data-testid="form-country" className="mt-1.5"><SelectValue placeholder="Select country" /></SelectTrigger>
              <SelectContent>
                {countries.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Zip Code</Label>
            <Input data-testid="form-zip" value={form.zip_code} onChange={set("zip_code")} className="mt-1.5" placeholder="Enter PIN to auto-fill city & state" />
          </div>
          <div>
            <Label>City</Label>
            <Input data-testid="form-city" value={form.city} readOnly disabled
                   className="mt-1.5 bg-[#F4F6F4] text-muted-foreground cursor-not-allowed" placeholder="Auto-filled from PIN" />
          </div>
          <div>
            <Label>State</Label>
            <Input data-testid="form-state" value={form.state} readOnly disabled
                   className="mt-1.5 bg-[#F4F6F4] text-muted-foreground cursor-not-allowed" placeholder="Auto-filled from PIN" />
            <p className="text-[11px] text-muted-foreground mt-1">City &amp; State auto-fill from the PIN code</p>
          </div>
          <div>
            <Label>Pickup Date</Label>
            <Input type="date" data-testid="form-pickup-date" value={form.pickup_date} onChange={set("pickup_date")} className="mt-1.5" />
          </div>
          <div>
            <Label>Initial EDD</Label>
            <Input type="date" data-testid="form-initial-edd" value={form.initial_edd} onChange={set("initial_edd")} className="mt-1.5" />
          </div>
          <div>
            <Label>Assign To</Label>
            <Select value={form.assigned_to || "unassigned"} onValueChange={(v) => setForm((f) => ({ ...f, assigned_to: v === "unassigned" ? "" : v }))}>
              <SelectTrigger data-testid="form-assigned" className="mt-1.5"><SelectValue placeholder="Unassigned" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="unassigned">Unassigned</SelectItem>
                {(agents || []).map((a) => (
                  <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2">
            <Label>Notes</Label>
            <div className="relative">
              <Textarea data-testid="form-notes" value={form.notes} ref={notesRef}
                        onChange={handleNotesChange} onKeyDown={handleNotesKeyDown}
                        className="mt-1.5" rows={2} placeholder="Add a note… type @ to tag a teammate" />
              {mentionOpen && mentionMatches.length > 0 && (
                <div className="absolute z-30 left-0 right-0 sm:right-auto sm:w-72 mt-1 rounded-md border border-border bg-white shadow-lg overflow-hidden"
                     data-testid="form-mention-dropdown">
                  <p className="text-[11px] text-muted-foreground px-3 py-1.5 border-b border-border">Tag a teammate — they&apos;ll be notified</p>
                  <div className="max-h-56 overflow-auto py-1">
                    {mentionMatches.map((m, i) => (
                      <button key={m.id} type="button" data-testid={`form-mention-opt-${m.id}`}
                              onMouseDown={(e) => { e.preventDefault(); insertMention(m); }}
                              onMouseEnter={() => setMentionIndex(i)}
                              className={`w-full text-left flex items-center gap-2 px-3 py-1.5 text-sm ${i === mentionIndex ? "bg-[#F0F2F0]" : "hover:bg-[#F0F2F0]"}`}>
                        <AtSign className="h-3.5 w-3.5 text-[#0B3D3D] shrink-0" />
                        <span className="truncate">{m.name} <span className="text-xs text-muted-foreground capitalize">· {m.role_label || m.role}</span></span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            {!editing && tagged.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5 mt-1.5" data-testid="form-tagged-list">
                {tagged.map((uid) => {
                  const m = team.find((x) => x.id === uid);
                  return (
                    <span key={uid} className="inline-flex items-center gap-1 text-xs bg-[#E4ECF7] text-[#1D4E89] rounded-full pl-2 pr-1 py-0.5">
                      @{m?.name || "user"}
                      <button type="button" data-testid={`form-untag-${uid}`} onClick={() => setTagged((t) => t.filter((x) => x !== uid))}
                              className="hover:text-[#C24127] rounded-full leading-none">×</button>
                    </span>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="rounded-md border border-border bg-[#F9F9F8] p-3">
          <p className="text-xs font-medium text-muted-foreground mb-1">Auto-generated tracking link</p>
          {trackingLink ? (
            <a href={trackingLink} target="_blank" rel="noreferrer"
               data-testid="form-tracking-link-preview"
               className="text-sm text-[#0B3D3D] font-medium inline-flex items-center gap-1 break-all hover:underline">
              {trackingLink} <ExternalLink className="h-3 w-3 shrink-0" />
            </a>
          ) : (
            <p className="text-sm text-muted-foreground">Enter courier & tracking number to generate a link.</p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="form-cancel">Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="form-save" className="bg-[#0B3D3D] hover:bg-[#0A2E2E] text-white">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {editing ? "Save changes" : "Add shipment"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
